import type { ContentFitnessDimensionResponse } from "contracts/api/dashboard/responses";
import type { Issue } from "contracts/api/issue/responses";
import {
  CONTENT_FITNESS_COVERAGE_STATUS,
  CONTENT_FITNESS_VERIFICATION_STATE,
} from "contracts/enums/content-fitness";
import {
  contentFitnessCanonicalCountForDimension,
  contentFitnessSubcriterionForKey,
} from "contracts/report/content-fitness/lookups";
import { rollUpContentFitness } from "contracts/report/content-fitness/rollup";
import type { ContentFitnessSubcriterion } from "contracts/report/content-fitness/subcriteria/types";
import { rawPagePath } from "../pages/boundaries/page-label.parser";
import { coverageLabel as clientCoverageLabel } from "./clientLanguage";
import type { OverviewPage, PillarSignal } from "./scanModel";

export type DimensionAssessment =
  ContentFitnessDimensionResponse["assessments"][number];

export type AssessmentRow = {
  page: OverviewPage;
  assessment: DimensionAssessment;
  subcriterion: ContentFitnessSubcriterion;
};

/** Every persisted pilot assessment for one dimension, sorted by order then page. */
export function assessmentRowsForDimension(
  assessments: DimensionAssessment[],
  pagesById: Map<string, OverviewPage>,
  pilotKeys: Set<string>,
): AssessmentRow[] {
  return assessments
    .flatMap((assessment): AssessmentRow[] => {
      if (!pilotKeys.has(assessment.subcriterionKey)) {
        return [];
      }
      const page = assessment.pageId
        ? pagesById.get(assessment.pageId)
        : undefined;
      const subcriterion = contentFitnessSubcriterionForKey(
        assessment.subcriterionKey,
      );
      return page && subcriterion ? [{ page, assessment, subcriterion }] : [];
    })
    .sort(
      (left, right) =>
        left.subcriterion.order - right.subcriterion.order ||
        rawPagePath(left.page).localeCompare(rawPagePath(right.page)),
    );
}

export function uniquePages(pages: OverviewPage[]): OverviewPage[] {
  const seen = new Set<string>();
  const rows: OverviewPage[] = [];
  for (const page of pages) {
    if (seen.has(page.id)) {
      continue;
    }
    seen.add(page.id);
    rows.push(page);
  }
  return rows;
}

export function pageDimensionScore(
  assessments: DimensionAssessment[],
  pageId: string,
  dimensionKey: string,
): number | null {
  const pageRows = assessments.filter((row) => row.pageId === pageId);
  if (pageRows.length === 0) {
    return null;
  }
  const rollup = rollUpContentFitness(
    pageRows.map((row) => ({
      subcriterionKey: row.subcriterionKey,
      verificationState: row.verificationState,
      score: row.score,
    })),
  );
  return (
    rollup.dimensions.find((dimension) => dimension.key === dimensionKey)
      ?.score ?? null
  );
}

export function dimensionCoverageStatus(
  signal: PillarSignal | null,
  assessments: DimensionAssessment[],
  dimensionKey: string,
): string {
  if (signal?.coverageStatus) {
    return coverageWord(signal);
  }
  const scored = new Set(
    assessments
      .filter(
        (row) =>
          row.verificationState === CONTENT_FITNESS_VERIFICATION_STATE.SCORED,
      )
      .map((row) => row.subcriterionKey),
  );
  if (scored.size === 0) return clientCoverageLabel(null);
  const canonical = contentFitnessCanonicalCountForDimension(dimensionKey);
  return clientCoverageLabel(
    scored.size === canonical
      ? CONTENT_FITNESS_COVERAGE_STATUS.FULL
      : CONTENT_FITNESS_COVERAGE_STATUS.PARTIAL,
  );
}

export function issueBelongsToDimension(
  issue: Issue,
  dimensionKey: string,
): boolean {
  const subcriterion = issue.primarySubcriterionKey
    ? contentFitnessSubcriterionForKey(issue.primarySubcriterionKey)
    : null;
  return subcriterion?.parentMetricKey === dimensionKey;
}

export function issueContributedToDimensionScore(
  issue: Issue,
  assessments: DimensionAssessment[],
): boolean {
  if (!issue.primarySubcriterionKey) return false;
  return assessments.some(
    (assessment) =>
      assessment.subcriterionKey === issue.primarySubcriterionKey &&
      assessment.verificationState ===
        CONTENT_FITNESS_VERIFICATION_STATE.SCORED &&
      assessment.score !== null,
  );
}

export function subcriterionLabel(key: string | null): string {
  if (!key) {
    return "No primary subcriterion";
  }
  return contentFitnessSubcriterionForKey(key)?.label ?? key;
}

export function coverageWord(signal: PillarSignal | null): string {
  return clientCoverageLabel(signal?.coverageStatus ?? null);
}

export function rowsForSubcriterion(
  rows: AssessmentRow[],
  key: string,
): AssessmentRow[] {
  return rows.filter((row) => row.assessment.subcriterionKey === key);
}
